import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat, SINGLE_PLAYER } from '../bridge/seats.js';
import { seededRandom, shuffled } from '../trick/rng.js';

export type ColourDashSeat = Seat;

export class IllegalColourDashMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalColourDashMoveError';
  }
}

export type DashColour = 'red' | 'yellow' | 'green' | 'blue';
export const DASH_COLOURS: readonly DashColour[] = ['red', 'yellow', 'green', 'blue'];

export type DashKind = 'number' | 'skip' | 'reverse' | 'draw-two' | 'wild' | 'wild-draw-four';

/**
 * One card of the 108-card Colour Dash deck. `id` is unique (0..107) and stable for the whole game. A number card has a `value` 0-9; an
 * action card has a colour and no value; the two wild kinds have neither.
 */
export interface DashCard {
  readonly id: number;
  readonly kind: DashKind;
  readonly colour: DashColour | null;
  readonly value: number | null;
}

export const sameDashCard = (a: DashCard, b: DashCard): boolean => a.id === b.id;
export const isDashWild = (card: DashCard): boolean => card.kind === 'wild' || card.kind === 'wild-draw-four';

/**
 * The deck: per colour one 0 and two each of 1-9 (19 numbers), plus two each of Skip, Reverse and Draw Two (6), 25 a colour and 100 in all,
 * plus four Wild and four Wild Draw Four: 108.
 */
export function colourDashDeck(): DashCard[] {
  const cards: Omit<DashCard, 'id'>[] = [];
  for (const colour of DASH_COLOURS) {
    cards.push({ kind: 'number', colour, value: 0 });
    for (let v = 1; v <= 9; v++) cards.push({ kind: 'number', colour, value: v }, { kind: 'number', colour, value: v });
    for (const kind of ['skip', 'reverse', 'draw-two'] as const) cards.push({ kind, colour, value: null }, { kind, colour, value: null });
  }
  for (let i = 0; i < 4; i++) cards.push({ kind: 'wild', colour: null, value: null }, { kind: 'wild-draw-four', colour: null, value: null });
  return cards.map((c, id) => ({ ...c, id }));
}

const KIND_ORDER: Record<DashKind, number> = { number: 0, skip: 1, reverse: 2, 'draw-two': 3, wild: 4, 'wild-draw-four': 5 };

/** A hand in a steady order: by colour, then numbers low to high, then the actions, wilds last. */
export function sortDashHand(cards: readonly DashCard[]): DashCard[] {
  const colourIx = (c: DashCard) => (c.colour === null ? 4 : DASH_COLOURS.indexOf(c.colour));
  return [...cards].sort((a, b) => colourIx(a) - colourIx(b) || KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || (a.value ?? 0) - (b.value ?? 0) || a.id - b.id);
}

/** A name a screen reader can say: "red 5", "blue skip", "wild draw four". */
export function dashCardName(card: DashCard): string {
  if (card.kind === 'wild') return 'wild';
  if (card.kind === 'wild-draw-four') return 'wild draw four';
  return `${card.colour} ${card.kind === 'number' ? card.value : card.kind.replace('-', ' ')}`;
}

/** What a card is worth against the player who goes out: a number its face value, an action 20, a wild 50. */
export function dashCardPoints(card: DashCard): number {
  if (card.kind === 'number') return card.value as number;
  return isDashWild(card) ? 50 : 20;
}

export type ColourDashPhase = 'play' | 'done';

/** Something that happened on the last action, for the screen to show. Counts only: it never says which cards were drawn. */
export type DashEvent =
  | { readonly type: 'skipped'; readonly seat: Seat }
  | { readonly type: 'reversed' }
  | { readonly type: 'drew'; readonly seat: Seat; readonly count: number; readonly reason: 'draw' | 'draw-two' | 'wild-draw-four' | 'last-card' }
  | { readonly type: 'colour'; readonly colour: DashColour };

export interface ColourDashOptions {
  /**
   * When true, Wild Draw Four may be played only by a hand with no card of the colour in force (the legality half of the usual rule). There is no
   * challenge step. Off by default: the simple rule lets it be played any time.
   */
  readonly restrictWildDrawFour: boolean;
}
export const DEFAULT_COLOUR_DASH_OPTIONS: ColourDashOptions = { restrictWildDrawFour: false };

/**
 * Colour Dash: match the card on top of the pile by colour, number or symbol, or play a wild and name the colour. Skip passes the next player,
 * Reverse turns the direction (it acts as Skip with two players), Draw Two and Wild Draw Four make the next player draw 2 or 4 and miss a turn.
 * If you cannot play, draw one card and play it if you can. On your second-to-last card you must call "last card" or draw two. The first to
 * empty their hand wins the hand and scores the cards left in the others' hands. No stakes: points are only a score.
 *
 * The state holds every hand and the stock; clients and robots read only `colourDashViewFor` (view.ts).
 */
export interface ColourDashGame {
  readonly seed: number;
  readonly dealer: Seat;
  /** The seats in play, clockwise (2 to 4 of the four table seats). */
  readonly seats: readonly Seat[];
  readonly controllers: BridgeControllers;
  readonly options: ColourDashOptions;
  readonly hands: Readonly<Partial<Record<Seat, readonly DashCard[]>>>;
  /** Face-down cards, next card last. */
  readonly stock: readonly DashCard[];
  /** The pile; the card on top is the last. */
  readonly discard: readonly DashCard[];
  /** The colour in force: the top card's colour, or the one named after a wild. */
  readonly colour: DashColour;
  /** 1 is the table order of `seats`, -1 the reverse. */
  readonly direction: 1 | -1;
  readonly phase: ColourDashPhase;
  readonly turn: Seat;
  /** True once the seat to play has drawn this turn (it may not draw again). */
  readonly drew: boolean;
  /** True once the seat to play has called "last card" this turn. */
  readonly calledLast: boolean;
  /** How many seats in a row have passed with nothing to play or draw; a full circle ends the hand. */
  readonly stuck: number;
  readonly winner: Seat | null;
  /** What the last action did (replaced by the next action). */
  readonly events: readonly DashEvent[];
}

export interface NewColourDashOptions {
  readonly seed: number;
  /** 2 to 4 seats, in table order. Defaults to all four. */
  readonly seats?: readonly Seat[];
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
  readonly options?: Partial<ColourDashOptions>;
}

export const COLOUR_DASH_HAND_SIZE = 7;

/** The seat `steps` places from `seat` in the given direction. */
function stepSeat(seats: readonly Seat[], seat: Seat, direction: 1 | -1, steps = 1): Seat {
  const n = seats.length;
  return seats[(((seats.indexOf(seat) + direction * steps) % n) + n) % n] as Seat;
}

export function newColourDashGame(opts: NewColourDashOptions): ColourDashGame {
  const seats = (opts.seats ?? BRIDGE_SEATS).filter((s, i, a) => a.indexOf(s) === i);
  if (seats.length < 2 || seats.length > 4) throw new IllegalColourDashMoveError(`Colour Dash is for 2 to 4 players, not ${seats.length}`);
  const dealer = opts.dealer && seats.includes(opts.dealer) ? opts.dealer : (seats[0] as Seat);
  let deck = shuffled(colourDashDeck(), seededRandom(opts.seed));
  const hands: Record<string, DashCard[]> = Object.fromEntries(seats.map((s) => [s, [] as DashCard[]]));
  const order = [...seats.slice(seats.indexOf(dealer) + 1), ...seats.slice(0, seats.indexOf(dealer) + 1)];
  for (let r = 0; r < COLOUR_DASH_HAND_SIZE; r++) for (const s of order) (hands[s] as DashCard[]).push(deck.pop() as DashCard);
  // The first card of the pile is a plain number card: tuck any action or wild under the stock and turn up the next.
  let first = deck.pop() as DashCard;
  while (first.kind !== 'number') {
    deck = [first, ...deck];
    first = deck.pop() as DashCard;
  }
  return {
    seed: opts.seed,
    dealer,
    seats,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    options: { ...DEFAULT_COLOUR_DASH_OPTIONS, ...opts.options },
    hands: Object.fromEntries(seats.map((s) => [s, sortDashHand(hands[s] as DashCard[])])),
    stock: deck,
    discard: [first],
    colour: first.colour as DashColour,
    direction: 1,
    phase: 'play',
    turn: stepSeat(seats, dealer, 1, 1),
    drew: false,
    calledLast: false,
    stuck: 0,
    winner: null,
    events: [],
  };
}

export const colourDashTop = (game: ColourDashGame): DashCard => game.discard[game.discard.length - 1] as DashCard;

/** The seat that plays after the seat to play, in the direction of play now (a Reverse will change who that is). */
export const colourDashNextSeat = (game: ColourDashGame): Seat => stepSeat(game.seats, game.turn, game.direction);

const handOf = (game: ColourDashGame, seat: Seat): readonly DashCard[] => game.hands[seat] ?? [];

/** Does `card` go on the current top card: the colour in force, the same number, the same symbol, or a wild (Wild Draw Four may be restricted). */
function fits(game: ColourDashGame, card: DashCard, hand: readonly DashCard[]): boolean {
  if (card.kind === 'wild') return true;
  if (card.kind === 'wild-draw-four') return !game.options.restrictWildDrawFour || !hand.some((c) => c.colour === game.colour);
  if (card.colour === game.colour) return true;
  const top = colourDashTop(game);
  if (card.kind === 'number') return top.kind === 'number' && top.value === card.value;
  return top.kind === card.kind;
}

/** The cards the seat to play may play now. */
export function colourDashLegalPlays(game: ColourDashGame): DashCard[] {
  if (game.phase !== 'play') return [];
  const hand = handOf(game, game.turn);
  return hand.filter((c) => fits(game, c, hand));
}

/** Cards that can still be drawn: the stock, plus the pile under its top card (reshuffled when the stock runs out). */
const drawable = (game: ColourDashGame): number => game.stock.length + Math.max(0, game.discard.length - 1);

export const colourDashCanDraw = (game: ColourDashGame): boolean =>
  game.phase === 'play' && !game.drew && colourDashLegalPlays(game).length === 0 && drawable(game) > 0;

/** May the seat to play call "last card" now: it holds exactly two cards and has not called yet. */
export const colourDashCanCallLast = (game: ColourDashGame): boolean =>
  game.phase === 'play' && !game.calledLast && handOf(game, game.turn).length === 2;

/** Take up to `count` cards from the stock into `seat`'s hand, recycling the pile (under its top card) when the stock runs out. */
function drawCards(game: ColourDashGame, seat: Seat, count: number): { game: ColourDashGame; drew: number } {
  let stock = [...game.stock];
  let discard = [...game.discard];
  const hand = [...handOf(game, seat)];
  let drew = 0;
  for (let i = 0; i < count; i++) {
    if (stock.length === 0 && discard.length > 1) {
      const keep = discard[discard.length - 1] as DashCard;
      stock = shuffled(discard.slice(0, -1), seededRandom(game.seed + discard.length));
      discard = [keep];
    }
    if (stock.length === 0) break;
    hand.push(stock.pop() as DashCard);
    drew++;
  }
  return { game: { ...game, stock, discard, hands: { ...game.hands, [seat]: sortDashHand(hand) } }, drew };
}

const handValue = (cards: readonly DashCard[]): number => cards.reduce((a, c) => a + dashCardPoints(c), 0);

/** A blocked table: the seat with the lowest hand value wins (the first of them in table order on a tie). */
function endStuck(game: ColourDashGame): ColourDashGame {
  const best = game.seats.reduce((a, b) => (handValue(handOf(game, b)) < handValue(handOf(game, a)) ? b : a));
  return { ...game, phase: 'done', winner: best };
}

/** Move on to the next seat; a seat that cannot play or draw passes at once, and a full circle of passes ends the hand. */
function settle(game: ColourDashGame): ColourDashGame {
  let g = game;
  while (g.phase === 'play' && colourDashLegalPlays(g).length === 0 && !colourDashCanDraw(g) && (g.drew || drawable(g) === 0)) {
    const stuck = g.stuck + 1;
    if (stuck >= g.seats.length) return endStuck({ ...g, stuck });
    g = { ...g, turn: stepSeat(g.seats, g.turn, g.direction), drew: false, calledLast: false, stuck };
  }
  return g;
}

/**
 * Play a card. A wild needs `colour` (the colour you name); any other card must not carry one. The card's effect happens at once: Skip and
 * Reverse (with two players) mean the next player misses a turn, Draw Two and Wild Draw Four make them draw and miss a turn. Leaving one card
 * without having called "last card" costs two cards.
 */
export function colourDashPlay(game: ColourDashGame, card: DashCard, colour?: DashColour): ColourDashGame {
  if (game.phase !== 'play') throw new IllegalColourDashMoveError('the hand is over');
  if (!colourDashLegalPlays(game).some((c) => sameDashCard(c, card))) throw new IllegalColourDashMoveError(`${game.turn} cannot play that card now`);
  const wild = isDashWild(card);
  if (wild && (colour === undefined || !DASH_COLOURS.includes(colour))) throw new IllegalColourDashMoveError('a wild needs the colour you name');
  if (!wild && colour !== undefined) throw new IllegalColourDashMoveError('only a wild names a colour');
  const seat = game.turn;
  const events: DashEvent[] = [];
  const hand = handOf(game, seat).filter((c) => !sameDashCard(c, card));
  let g: ColourDashGame = {
    ...game,
    hands: { ...game.hands, [seat]: hand },
    discard: [...game.discard, card],
    colour: wild ? (colour as DashColour) : (card.colour as DashColour),
    drew: false,
    stuck: 0,
  };
  if (wild) events.push({ type: 'colour', colour: colour as DashColour });
  if (card.kind === 'reverse') {
    g = { ...g, direction: g.direction === 1 ? -1 : 1 };
    events.push({ type: 'reversed' });
  }
  // Who is next, and who is hit: with two players Reverse acts as Skip.
  const next = stepSeat(g.seats, seat, g.direction);
  let after = next;
  if (card.kind === 'skip' || (card.kind === 'reverse' && g.seats.length === 2)) {
    events.push({ type: 'skipped', seat: next });
    after = stepSeat(g.seats, next, g.direction);
  } else if (card.kind === 'draw-two' || card.kind === 'wild-draw-four') {
    const hit = drawCards(g, next, card.kind === 'draw-two' ? 2 : 4);
    g = hit.game;
    events.push({ type: 'drew', seat: next, count: hit.drew, reason: card.kind }, { type: 'skipped', seat: next });
    after = stepSeat(g.seats, next, g.direction);
  }
  if (hand.length === 0) return { ...g, phase: 'done', winner: seat, calledLast: false, events };
  if (hand.length === 1 && !game.calledLast) {
    const pen = drawCards(g, seat, 2);
    g = pen.game;
    events.push({ type: 'drew', seat, count: pen.drew, reason: 'last-card' });
  }
  return settle({ ...g, turn: after, calledLast: false, events });
}

/** Say "last card": allowed on your turn while you hold two cards. Do it before you play the second-to-last one. */
export function colourDashCallLast(game: ColourDashGame): ColourDashGame {
  if (!colourDashCanCallLast(game)) throw new IllegalColourDashMoveError('you can only call last card while holding two cards');
  return { ...game, calledLast: true, events: [] };
}

/** Draw one card (only when nothing is playable). If the drawn card cannot be played the turn ends; otherwise you may play it or pass. */
export function colourDashDraw(game: ColourDashGame): ColourDashGame {
  if (!colourDashCanDraw(game)) throw new IllegalColourDashMoveError('you cannot draw now');
  const seat = game.turn;
  const { game: g } = drawCards(game, seat, 1);
  return settle({ ...g, drew: true, stuck: 0, events: [{ type: 'drew', seat, count: 1, reason: 'draw' }] });
}

/** Decline to play the card you just drew. */
export function colourDashPass(game: ColourDashGame): ColourDashGame {
  if (game.phase !== 'play' || !game.drew) throw new IllegalColourDashMoveError('you can only pass after drawing');
  return settle({ ...game, turn: stepSeat(game.seats, game.turn, game.direction), drew: false, calledLast: false, events: [] });
}

export interface ColourDashResult {
  readonly winner: Seat;
  /** The winner's score for the hand: the other hands' card points (a blocked table: the others' minus the winner's own). */
  readonly points: number;
  /** How many cards each seat still holds. */
  readonly cardsLeft: Readonly<Partial<Record<Seat, number>>>;
  readonly blocked: boolean;
}

export function colourDashResult(game: ColourDashGame): ColourDashResult | null {
  if (game.phase !== 'done' || !game.winner) return null;
  const winner = game.winner;
  const others = game.seats.filter((s) => s !== winner).reduce((a, s) => a + handValue(handOf(game, s)), 0);
  const blocked = handOf(game, winner).length > 0;
  return {
    winner,
    points: Math.max(0, others - (blocked ? handValue(handOf(game, winner)) : 0)),
    cardsLeft: Object.fromEntries(game.seats.map((s) => [s, handOf(game, s).length])),
    blocked,
  };
}

/** Scores add up over hands; the first to reach the target wins the game. */
export const COLOUR_DASH_TARGET = 500;
export type ColourDashTotals = Readonly<Partial<Record<Seat, number>>>;

export function addColourDashHand(totals: ColourDashTotals, result: ColourDashResult): ColourDashTotals {
  return { ...totals, [result.winner]: (totals[result.winner] ?? 0) + result.points };
}

/** The seat that has reached the target with the highest total, or null while the game goes on. */
export function colourDashGameWinner(totals: ColourDashTotals, target = COLOUR_DASH_TARGET): Seat | null {
  const entries = Object.entries(totals).filter((e): e is [Seat, number] => typeof e[1] === 'number');
  const best = Math.max(0, ...entries.map(([, v]) => v));
  if (best < target) return null;
  const leaders = entries.filter(([, v]) => v === best);
  return leaders.length === 1 ? (leaders[0] as [Seat, number])[0] : null; // a tie at the top plays another hand
}
